import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const globalStyles = `
  *, *:hover, *:active, *:focus {
    cursor: none !important;
  }
`;

const CustomCursor = () => {
    const cursorRef = useRef(null);
    const [isVisible, setIsVisible] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const mousePosition = useRef({ x: 0, y: 0 });
    const smoothPosition = useRef({ x: 0, y: 0 });

    const location = useLocation();

    const isCustomCursorEnabled =
        (localStorage.getItem("customCursor") === null || localStorage.getItem("customCursor") === "false") &&
        window.innerWidth > 768;

    const lerp = (start, end, factor) => start + (end - start) * factor;

    const updateCursorPosition = () => {
        const cursor = cursorRef.current;
        const { x, y } = mousePosition.current;
        const smoothX = lerp(smoothPosition.current.x, x, 0.1);
        const smoothY = lerp(smoothPosition.current.y, y, 0.1);

        smoothPosition.current = { x: smoothX, y: smoothY };

        if (cursor) {
            cursor.style.left = `${smoothX}px`;
            cursor.style.top = `${smoothY}px`;
        }

        requestAnimationFrame(updateCursorPosition);
    };

    const throttle = (callback, limit) => {
        let lastCall = 0;
        return function (...args) {
            const now = Date.now();
            if (now - lastCall < limit) return;
            lastCall = now;
            return callback(...args);
        };
    };

    const handleMouseMove = throttle((event) => {
        mousePosition.current = { x: event.clientX, y: event.clientY };
    }, 8);

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    const handlePointerEnter = () => {
        setIsActive(true);
    };

    const handlePointerLeave = () => {
        setIsActive(false);
    };

    const debounce = (callback, delay) => {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => callback(...args), delay);
        };
    };

    const addEventListenersToInteractiveElements = debounce(() => {
        const interactiveElements = document.querySelectorAll(
            'a, button, input, textarea, [role="button"], [role="link"], [data-interactive="true"]'
        );

        interactiveElements.forEach((el) => {
            el.addEventListener("pointerenter", handlePointerEnter);
            el.addEventListener("pointerleave", handlePointerLeave);
        });
    }, 100);

    useEffect(() => {
        if (!isCustomCursorEnabled) return;

        const styleElement = document.createElement('style');
        styleElement.type = 'text/css';
        styleElement.appendChild(document.createTextNode(globalStyles));

        document.head.appendChild(styleElement);

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseenter", handleMouseEnter);
        document.addEventListener("mouseleave", handleMouseLeave);

        updateCursorPosition();

        addEventListenersToInteractiveElements();

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseenter", handleMouseEnter);
            document.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [isCustomCursorEnabled]);

    useEffect(() => {
        if (!isCustomCursorEnabled) return;

        addEventListenersToInteractiveElements();

        const observer = new MutationObserver(() => {
            addEventListenersToInteractiveElements();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [location, isCustomCursorEnabled]);

    useEffect(() => {
        if (!isCustomCursorEnabled) return;

        setIsActive(false);
    }, [location, isCustomCursorEnabled]);

    const cursorStyle = {
        width: isActive ? "50px" : "30px",
        height: isActive ? "50px" : "30px",
        borderRadius: "50%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        position: "fixed",
        pointerEvents: "none",
        zIndex: 99999999,
        transform: "translate(-50%, -50%)",
        transition: "opacity 0.15s ease-out, box-shadow 0.15s ease-out",
        opacity: isVisible ? 1 : 0,
        boxShadow: isVisible ? "0 0 15px rgba(80, 80, 80, 0.1)" : "none",
    };

    if (!isCustomCursorEnabled) return null;

    return (
        <motion.div
            ref={cursorRef}
            style={cursorStyle}
            animate={{
                width: isActive ? "50px" : "30px",
                height: isActive ? "50px" : "30px",
                opacity: isVisible ? 1 : 0,
                boxShadow: isVisible ? "0 0 15px rgba(80, 80, 80, 0.1)" : "none",
            }}
            transition={{ duration: 0.15 }}
        />
    );
};

export default CustomCursor;
